import { TachographCompanyCard, TachographDataFile } from "@fairfleet/geotab";

/** The tabs of the tachograph page, in display order. */
export const TACHOGRAPH_TABS = [
  "status",
  "driver",
  "downloads",
  "companyCards",
  "connectors",
] as const;

export type TachographTab = (typeof TACHOGRAPH_TABS)[number];

export type TachographStatus = "ok" | "warning" | "overdue";

/** What the counters above the table narrow the vehicle list down to. */
export type TachographSummaryFilter = "all" | "downloads" | "connectors";

export type TachographConnectivity = "online" | "degraded" | "offline";

export type CompanyCardStatus = "available" | "busy" | "offline" | "unknown";

/** The makes the tachograph itself reports. */
export type TachographManufacturer =
  | "notAvailable"
  | "unknown"
  | "vdo"
  | "stoneridge"
  | "actia"
  | "efas";

/** What the tachograph of a vehicle says about itself. */
export type TachographDeviceInfo = {
  manufacturer: TachographManufacturer | null;
  nextCalibration: Date | null;
  /** Whether remote download (RDL) is switched on; undefined when it was not reported. */
  remoteDownload?: boolean;
  /** Raw "Tachograph download task status"; Geotab does not document the numbers yet. */
  downloadTaskStatus?: number;
  /** Raw "Tachograph status"; Geotab does not document the numbers yet. */
  tachographStatus?: number;
  /** When the tachograph last reported anything — this is the tachograph's own signal. */
  lastReading: Date | null;
};

/** A tachograph data file as the API returns it — any field may be missing. */
export type TachographFile = Partial<TachographDataFile>;

/** A company card as the API returns it — any field may be missing. */
export type CompanyCard = Partial<TachographCompanyCard>;

/** The download deadlines derived from the last file of a device or driver. */
export type TachographTimeline = {
  legalLimit: string;
  nextDownloadUtc: string;
  /** Raw counterpart of legalLimit/nextDownloadUtc, for chronological grid sorting. */
  dueDate: Date | null;
  /** Raw counterpart of updateTime, for chronological grid sorting. */
  updateDate: Date | null;
};

export type TachographRow = {
  id: string;
  summary: string;
  driver: string;
  licensePlate: string;
  vin: string;
  serialNumber: string;
  driverId?: string;
  manufacturer: string;
  legalLimit: string;
  nextDownloadUtc: string;
  dueDate: Date | null;
  updateDate: Date | null;
  calibrationExpiryDate: Date | null;
  /** Undefined when the tachograph did not report it. */
  remoteDownload: boolean | undefined;
  downloadTaskStatus: number | undefined;
  tachographStatus: number | undefined;
  /** Undefined when the source it is derived from failed to load. */
  downloadStatus: TachographStatus | undefined;
  connectorStatus: TachographConnectivity | undefined;
};

export type TachographDriverRow = {
  id: string;
  driver: string;
  driverCardNumber: string;
  licensePlate: string;
  /** No card readout in the file window: overdue, but there is no due date to show. */
  noReadout: boolean;
  /** Undefined when the source it is derived from failed to load. */
  downloadStatus: TachographStatus | undefined;
  legalLimit: string;
  nextDownloadUtc: string;
  dueDate: Date | null;
  updateDate: Date | null;
};

export type TachographDownloadRow = {
  id: string;
  source: string;
  fileName: string;
  unit: string;
  dataFromUtc: Date | null;
  dataToUtc: Date | null;
  createdAt: Date | null;
  addedAt: Date | null;
};

export type TachographCompanyCardRow = {
  id: string;
  cardNumber: string;
  companyName: string;
  icc: string;
  status: string;
  source: string;
  validFrom: Date | null;
  expiryDateUtc: Date | null;
  lastUpdate: Date | null;
};

/** The user's readout deadlines, the days a readout is flagged ahead and whether rows are coloured. */
export type TachographDueSettings = {
  deviceIntervalDays: number;
  driverIntervalDays: number;
  warningDays: number;
  highlightRows: boolean;
};

/** How often a driver card and a vehicle unit have to be read out. */
export type TachographPolicy = {
  driverIntervalDays: number;
  deviceIntervalDays: number;
  source: "geotab" | "fallback";
};

/** Name and license plate of the vehicle a file belongs to. */
export type TachographUnitLabel = {
  name: string;
  licensePlate: string;
};

/** The counters above the table; null while a count is not known (loading or failed), never 0. */
export type TachographSummary = {
  total: number | null;
  downloadsDue: number | null;
  connectorsIssue: number | null;
};

/** The separately loaded parts of the page, named in the loading hint and the error alert. */
export type TachographDataPart =
  | "devices"
  | "assignments"
  | "drivers"
  | "policy"
  | "deviceInfo"
  | "files"
  | "companyCards";
